/** @jsx jsx */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { css, keyframes, jsx } from '@compiled/react';
import { PopupContent } from '../../popup-content/index.tsx';
import { checkIsClamped, getClosestTextElement } from '../../../controllers/utils.tsx';

export type PopoverInternalProps = {
	content: ReactNode;
	contentElement?: HTMLElement | null;
	onUpdate: () => void;
	isReferenceHidden?: boolean;
	alwaysShow?: boolean;
	onHoverOut?: () => void;
};

const CELL_HOVER_DELAY = 200;

export const PopoverInternal = ({
	content,
	contentElement,
	onUpdate,
	isReferenceHidden,
	alwaysShow,
	onHoverOut,
}: PopoverInternalProps) => {
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	hoverTimeoutRef.current = setTimeout(() => {
		if (alwaysShow) {
			setIsPopupOpen(true);
		}

		const elementWithTextContent = contentElement
			? getClosestTextElement(contentElement)
			: undefined;

		const clientWidth = elementWithTextContent?.clientWidth || 0;
		const scrollWidth = elementWithTextContent?.scrollWidth || 0;
		const isClamped = checkIsClamped(elementWithTextContent);

		if (!contentElement || scrollWidth > clientWidth || isClamped) {
			setIsPopupOpen(true);
		}
	}, CELL_HOVER_DELAY);

	useEffect(() => {
		return () => {
			if (hoverTimeoutRef.current) {
				clearTimeout(hoverTimeoutRef.current);
			}

			if (onHoverOut) {
				onHoverOut();
			}
		};
	}, [onHoverOut]);

	if (isPopupOpen && content && !isReferenceHidden) {
		return (
			<div css={containerStyles}>
				<PopupContent update={onUpdate}>{content}</PopupContent>
			</div>
		);
	}
};

const fadeIn = keyframes({
	from: {
		opacity: '0',
	},
	to: {
		opacity: '100',
	},
});

const containerStyles = css({
	animationName: `${fadeIn}`,
	opacity: '0',
	animationDuration: '0.1s',
	animationTimingFunction: 'ease-in',
	animationFillMode: 'forwards',
	maxWidth: '300px',
	pointerEvents: 'none',
});
